import { Component } from '@theme/component';

/**
 * Renders a live Days/Hours/Minutes/Seconds countdown toward a fixed, merchant-configured
 * end date. The end date is an absolute instant (an ISO 8601 string with a timezone offset),
 * so every visitor sees an accurate countdown to the same real moment — this deliberately
 * does not "reset" or fabricate time once it reaches zero.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 *
 * @extends {Component<Refs>}
 */
export class CountdownTimer extends Component {
  requiredRefs = ['days', 'hours', 'minutes', 'seconds'];

  /** @type {number | undefined} */
  #intervalId;

  /** @type {number} */
  #endTime = 0;

  connectedCallback() {
    super.connectedCallback();

    const endDateAttribute = this.getAttribute('data-end-date') ?? '';
    const parsedEndTime = Date.parse(endDateAttribute);

    if (isNaN(parsedEndTime)) {
      console.warn(
        `[countdown-timer] Invalid or missing "data-end-date" ("${endDateAttribute}"). Expected an ISO 8601 date-time with a timezone offset, e.g. "2026-12-31T23:59:59+05:30".`
      );
      this.#expire();
      return;
    }

    this.#endTime = parsedEndTime;
    this.#tick();
    this.#intervalId = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#intervalId);
  }

  #tick() {
    const remainingMs = this.#endTime - Date.now();

    if (remainingMs <= 0) {
      this.#expire();
      return;
    }

    const totalSeconds = Math.floor(remainingMs / 1000);

    this.#render({
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    });
  }

  /** @param {{ days: number, hours: number, minutes: number, seconds: number }} units */
  #render(units) {
    this.refs.days.textContent = String(units.days).padStart(2, '0');
    this.refs.hours.textContent = String(units.hours).padStart(2, '0');
    this.refs.minutes.textContent = String(units.minutes).padStart(2, '0');
    this.refs.seconds.textContent = String(units.seconds).padStart(2, '0');
  }

  #expire() {
    window.clearInterval(this.#intervalId);
    this.#render({ days: 0, hours: 0, minutes: 0, seconds: 0 });

    this.dispatchEvent(new CustomEvent('countdown:expired', { bubbles: true }));

    const onExpire = this.getAttribute('data-on-expire');
    const bar = this.closest('.countdown-bar');

    if (onExpire === 'hide_bar' && bar instanceof HTMLElement) {
      bar.setAttribute('hidden', '');
    } else {
      this.setAttribute('hidden', '');
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
